import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import logoHorizontalUrl from "../logo-h.png";
import logoMarkUrl from "../logo.png";
import { cn } from "./utils.js";

export type ButtonTone = "dark" | "light" | "soft" | "ghost" | "accent";

const brandName = "Attenda";

export function ButtonLink({
  children,
  className,
  href,
  tone = "dark",
}: {
  children: ReactNode;
  className?: string;
  href: string;
  tone?: ButtonTone;
}) {
  const tones: Record<ButtonTone, string> = {
    dark: "bg-slate-950 text-white shadow-[0_12px_30px_rgba(15,23,42,0.22)] hover:bg-slate-800",
    light: "bg-white text-slate-950 shadow-[0_12px_28px_rgba(15,23,42,0.16)] hover:bg-slate-50",
    soft: "border border-slate-200 bg-white text-slate-900 hover:bg-slate-100",
    ghost: "text-slate-600 hover:bg-slate-100 hover:text-slate-950",
    accent: "bg-slate-950 text-white shadow-[0_10px_24px_rgba(15,23,42,0.14)] hover:bg-slate-800",
  };
  const base = "inline-flex h-10 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold transition active:scale-[0.98]";

  if (href.startsWith("/")) {
    return (
      <Link className={cn(base, tones[tone], className)} to={href}>
        {children}
      </Link>
    );
  }

  return (
    <a className={cn(base, tones[tone], className)} href={href}>
      {children}
    </a>
  );
}

export function BrandLogo({ variant = "compact" }: { variant?: "compact" | "horizontal" }) {
  if (variant === "horizontal") {
    return (
      <span className="brand-logo brand-logo-horizontal">
        <img alt={brandName} height={1024} src={logoHorizontalUrl} width={1536} />
      </span>
    );
  }

  return (
    <span className="brand-logo brand-logo-compact">
      <span className="brand-logo-mark">
        <img alt="" aria-hidden="true" height={1254} src={logoMarkUrl} width={1254} />
      </span>
      <span>{brandName}</span>
    </span>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="section-label">{children}</p>;
}

export function StatusPill({ children, tone = "neutral" }: { children: ReactNode; tone?: "green" | "blue" | "amber" | "neutral" }) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    blue: "bg-sky-50 text-sky-700 ring-sky-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    neutral: "bg-slate-100 text-slate-600 ring-slate-200",
  };

  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1", tones[tone])}>
      {children}
    </span>
  );
}

export function Surface({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cn("rounded-lg border border-slate-200 bg-white text-slate-950 shadow-sm", className)}>{children}</section>;
}
